import React, { FC } from 'react';

import cx from 'classnames';
import BlockContent from '@sanity/block-content-to-react';
import getDefaultCarouselModule from 'utils/getDefaultCarouselModule';

import { ArticlePortableText } from 'styled';
import ArticleBodyModuleSwitch from 'components/ArticleBodyModuleSwitch';
import ArticleRating from 'components/ArticleRating';
import ArticleUserStory from 'components/ArticleUserStory';
import ArticleUserStoryForm from 'components/ArticleUserStoryForm';
import CarouselModule from 'styled/components/modules/CarouselModule';
import ScrollableAnchorWrapper from 'components/articleBodyModules/ScrollableAnchorWrapper';

import {
  SansSerif,
  ListItem,
  ExternalLink,
  InternalLink,
} from 'constants/PortableTextSerializer';
import { DefaultReferencesSideNav } from 'constants/Default';
import Language from 'constants/Language';

import { ArticleContent as IArticleContent } from 'types';

interface Props {
  content: IArticleContent;
}

const ArticleContent: FC<Props> = ({ content }) => {
  const {
    overview,
    references,
    moreArticles,
    overviewLabel,
    overviewSlug,
  } = content;
  const articleUserStories = content.articleUserStories;
  const hideUserStory = content.hideUserStory;

  return (
    <div className="ArticleContent overflow-x-hidden color-black">
      <div className="article-content-padding pb4_5">
        {!!overview.length && (
          <ScrollableAnchorWrapper
            sideNav={{
              label: overviewLabel,
              slug: overviewSlug,
              id: overviewSlug,
            }}
          >
            <ArticlePortableText
              className="ArticleContent__overview md:col-11 article-content-width"
              fontStyleIsSecondaryMd={true}
            >
              <BlockContent
                blocks={overview}
                serializers={{
                  listItem: ListItem,
                  marks: { link: ExternalLink, internalLink: InternalLink },
                }}
              />
            </ArticlePortableText>
          </ScrollableAnchorWrapper>
        )}
        <div
          className={cx({
            'ArticleContent__body--without-overview': !overview.length,
          })}
        >
          <ArticleBodyModuleSwitch modules={content.bodyModules} />
        </div>
      </div>
      {!hideUserStory && (
        <ArticleUserStoryForm sharedStoryCount={articleUserStories.length} />
      )}
      {!hideUserStory && (
        <ArticleUserStory articleUserStories={articleUserStories} />
      )}
      <ArticleRating rating={content.articleRating} />
      {!!moreArticles.length && (
        <div className="bg-color-gray-lightest pt2_5 md:pt3">
          <div className="article-content-padding mb2 md:mb1_5 md:ml3">
            <span className="text-lg">
              {Language.t('ArticleContent.readThisNext')}
            </span>
          </div>
          <CarouselModule
            module={getDefaultCarouselModule('ORANGE', moreArticles)}
          />
        </div>
      )}
      {!!references.length && (
        <ScrollableAnchorWrapper sideNav={DefaultReferencesSideNav}>
          <div className="article-content-padding pt4 pb3_5 md:pt5_5 md:pb3_75 bg-color-gray-lightest">
            <ArticlePortableText className="ArticleContent__references-text-container md:col-10 article-content-width md:pl3">
              <h4>{Language.t('Global.references')}</h4>
              <BlockContent
                blocks={references}
                serializers={{
                  marks: {
                    sansSerif: SansSerif,
                    link: ExternalLink,
                    internalLink: InternalLink,
                  },
                  listItem: ListItem,
                }}
              />
            </ArticlePortableText>
          </div>
        </ScrollableAnchorWrapper>
      )}
    </div>
  );
};

export default ArticleContent;
